import {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from "react"
import {
  Modal,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native"
import { Gesture, GestureDetector } from "react-native-gesture-handler"
import Animated, { useAnimatedStyle, useSharedValue } from "react-native-reanimated"

export type TrueSheetRef = {
  present: (index?: number, animated?: boolean) => Promise<void>
  dismiss: (animated?: boolean) => Promise<void>
  resize: (index: number) => Promise<void>
  dismissStack: (animated?: boolean) => Promise<void>
}

/** Imperative handle type for `useRef<TrueSheet>(null)` on web. */
export type TrueSheet = TrueSheetRef

type TrueSheetProps = {
  ref?: Ref<TrueSheetRef>
  children?: ReactNode
  header?: ReactNode
  headerStyle?: StyleProp<ViewStyle>
  onDidDismiss?: () => void
  detents?: number[]
  cornerRadius?: number
  maxContentWidth?: number
  grabber?: boolean
  draggable?: boolean
}

const DISMISS_DRAG_THRESHOLD = 80
const DISMISS_VELOCITY_THRESHOLD = 600
/**
 * Web fallback: native TrueSheet cannot load in webpack. Presents children in a bottom Modal
 * when `present()` is called on the ref. Honors `detents` for sheet height.
 */
export function TrueSheet({
  ref,
  children,
  header,
  headerStyle,
  onDidDismiss,
  detents,
  cornerRadius = 20,
  maxContentWidth = 640,
  grabber = true,
  draggable = true,
}: TrueSheetProps) {
  const [open, setOpen] = useState(false)
  const isOpenRef = useRef(false)
  const translateY = useSharedValue(0)
  const isClosingSv = useSharedValue(false)
  const { height: windowHeight, width: windowWidth } = useWindowDimensions()

  const sheetHeight = useMemo(() => {
    const detent = detents?.[0] ?? 0.85
    return Math.round(windowHeight * Math.min(0.92, Math.max(0.4, detent)))
  }, [detents, windowHeight])

  const sheetWidth = Math.min(windowWidth, maxContentWidth)

  const finishDismiss = useCallback(() => {
    if (!isOpenRef.current) {
      return
    }
    isOpenRef.current = false
    setOpen(false)
    onDidDismiss?.()
  }, [onDidDismiss])

  const animateDismiss = useCallback(() => {
    if (!isOpenRef.current) {
      return Promise.resolve()
    }
    finishDismiss()
    return Promise.resolve()
  }, [finishDismiss])

  const present = useCallback(
    (_index?: number, _animated?: boolean) =>
      new Promise<void>((resolve) => {
        isOpenRef.current = true
        // isClosingRef.current = false
        // isClosingSv.value = false
        translateY.value = 0
        setOpen(true)
        resolve()
      }),
    [translateY]
  )

  useImperativeHandle(
    ref,
    () => ({
      present,
      dismiss: animateDismiss,
      resize: () => Promise.resolve(),
      dismissStack: () => Promise.resolve(),
    }),
    [animateDismiss, present]
  )

  const panGesture = useMemo(
    () =>
      Gesture.Pan()
        .enabled(draggable)
        .activeOffsetY(6)
        .failOffsetX([-24, 24])
        .onUpdate((event) => {
          if (isClosingSv.value) {
            return
          }
          translateY.value = Math.max(0, event.translationY)
        })
        .onEnd((event) => {
          if (isClosingSv.value) {
            return
          }
          if (
            event.translationY > DISMISS_DRAG_THRESHOLD ||
            event.velocityY > DISMISS_VELOCITY_THRESHOLD
          ) {
            animateDismiss()
            return
          }
        }),
    [animateDismiss, draggable, isClosingSv.value, translateY]
  )

  const sheetAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }))

  const sheetFrameStyle = useMemo(
    () => ({
      height: sheetHeight,
      width: sheetWidth,
      borderTopLeftRadius: cornerRadius,
      borderTopRightRadius: cornerRadius,
    }),
    [cornerRadius, sheetHeight, sheetWidth]
  )

  const dragHeader = (
    <GestureDetector gesture={panGesture}>
      <View style={styles.dragZone}>
        {grabber ? <View style={styles.grabber} /> : null}
        {header ? <View style={headerStyle}>{header}</View> : null}
      </View>
    </GestureDetector>
  )

  return (
    <Modal visible={open} transparent animationType="fade">
      <View style={styles.root}>
        <Pressable style={StyleSheet.absoluteFill} onPress={() => void animateDismiss()} />
        <Animated.View style={[styles.sheet, sheetFrameStyle, sheetAnimatedStyle]}>
          {draggable ? (
            dragHeader
          ) : (
            <>
              {grabber ? <View style={styles.grabber} /> : null}
              {header ? <View style={headerStyle}>{header}</View> : null}
            </>
          )}
          <View style={styles.content}>{children}</View>
        </Animated.View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: "flex-end",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  sheet: {
    backgroundColor: "white",
    overflow: "hidden",
    maxHeight: "92%",
  },
  dragZone: {
    cursor: "grab",
    userSelect: "none",
  } as unknown as ViewStyle,
  grabber: {
    alignSelf: "center",
    width: 40,
    height: 5,
    borderRadius: 3,
    backgroundColor: "#BCBBBD",
    marginTop: 10,
    marginBottom: 6,
  },
  content: {
    flex: 1,
    minHeight: 0,
  },
})
